import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import actualites from '../content/actualites.json';

export default function ActualiteDetailPage() {
  const { slug } = useParams();
  const article = actualites.find((item) => item.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <Navigation />
        <main className="max-w-3xl mx-auto px-4 pt-32 pb-24">
          <h1 className="text-3xl font-bold mb-4">Ressource introuvable</h1>
          <p className="text-slate-300 mb-8">Cette ressource n’est pas disponible. Retrouvez les guides dans la rubrique Actualités et ressources.</p>
          <Link to="/actualites" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200"><ArrowLeft aria-hidden="true" className="h-4 w-4" /> Consulter les ressources</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="actualite-detail-page">
      <Navigation />
      <main className="pt-28 pb-20 md:pt-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Fil d’Ariane" className="text-sm text-slate-400 mb-8">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <li><Link to="/" className="hover:text-white">Accueil</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/actualites" className="hover:text-white">Actualités et ressources</Link></li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-slate-200">{article.titre}</li>
            </ol>
          </nav>

          <article>
            <header className="max-w-4xl mb-12">
              <span className="inline-flex items-center gap-2 rounded-full bg-blue-500/15 border border-blue-400/25 px-4 py-2 text-sm text-blue-200 mb-5"><BookOpen aria-hidden="true" className="h-4 w-4" /> {article.categorie} · Guide pratique</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight mb-6">{article.titre}</h1>
              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed">{article.intro}</p>
            </header>

            <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(240px,1fr)] gap-10 lg:gap-16">
              <div className="space-y-10">
                {article.sections.map((section) => (
                  <section key={section.id} id={section.id} className="scroll-mt-28">
                    <h2 className="text-2xl font-bold mb-4">{section.titre}</h2>
                    <div className="space-y-4 text-slate-300 leading-relaxed">
                      {section.paragraphes.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                      {section.liste && <ul className="list-disc pl-5 space-y-3 marker:text-amber-300">{section.liste.map((item) => <li key={item}>{item}</li>)}</ul>}
                    </div>
                  </section>
                ))}
              </div>

              <aside className="space-y-6">
                <nav aria-label="Sommaire de la ressource" className="rounded-2xl bg-slate-900 border border-slate-800 p-6">
                  <h2 className="text-lg font-bold mb-4">Dans ce guide</h2>
                  <ol className="space-y-3">
                    {article.sections.map((section) => <li key={section.id}><a href={`#${section.id}`} className="text-slate-300 hover:text-amber-300 underline decoration-slate-600 underline-offset-4">{section.titre}</a></li>)}
                  </ol>
                </nav>
                <section className="rounded-2xl bg-slate-900 border border-slate-800 p-6" aria-labelledby="liens-utiles-title">
                  <h2 id="liens-utiles-title" className="text-lg font-bold mb-4">Pages de référence</h2>
                  <ul className="space-y-4">
                    {article.liens.map((link) => <li key={link.to}><Link to={link.to} className="inline-flex items-start gap-2 text-blue-200 hover:text-white"><ArrowRight aria-hidden="true" className="h-4 w-4 mt-1 shrink-0" />{link.label}</Link></li>)}
                  </ul>
                </section>
              </aside>
            </div>
          </article>

          <div className="mt-14 pt-8 border-t border-slate-800 flex flex-wrap gap-6 justify-between">
            <Link to="/actualites" className="inline-flex items-center gap-2 font-semibold text-amber-300 hover:text-amber-200"><ArrowLeft aria-hidden="true" className="h-4 w-4" /> Toutes les ressources</Link>
            <Link to="/contact" className="inline-flex items-center gap-2 font-semibold text-blue-200 hover:text-white">Présenter mon besoin <ArrowRight aria-hidden="true" className="h-4 w-4" /></Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
